/**
 * HYPSEYA — Pattern Intelligence
 * GPT-4o longitudinal analysis of the user's emotional regulation data.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Spark, Wave, Chart, Leaf, Cycle } from '@/components/HypIcons';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_STYLES = {
  'Temporal':     { color: '#2D8B87', bg: 'rgba(45,139,135,0.07)' },
  'Activation':   { color: '#1E6B68', bg: 'rgba(30,107,104,0.07)' },
  'Consistency':  { color: '#B8906A', bg: 'rgba(184,144,106,0.07)' },
  'Integration':  { color: '#4AACA8', bg: 'rgba(74,172,168,0.07)' },
  'Stability':    { color: '#7A9BA8', bg: 'rgba(122,155,168,0.07)' },
};

const VALENCE_BORDER = {
  positive:   '#2D8B87',
  neutral:    '#B8D3CF',
  attention:  '#B8906A',
};

function PatternCard({ pattern, index }) {
  const style  = CATEGORY_STYLES[pattern.category] || CATEGORY_STYLES['Stability'];
  const border = VALENCE_BORDER[pattern.valence] || VALENCE_BORDER.neutral;
  const delay  = index * 0.1;

  return (
    <div style={{
      background: '#fff', borderRadius: '18px', padding: '24px',
      marginBottom: '16px',
      boxShadow: '0 2px 16px rgba(30,107,104,0.06)',
      borderLeft: `3px solid ${border}`,
      animation: `hyp-fade-in 0.5s ease both`,
      animationDelay: `${delay}s`
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <span style={{
          fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.1em',
          textTransform: 'uppercase', color: style.color,
          background: style.bg, padding: '3px 10px', borderRadius: '20px'
        }}>
          {pattern.category}
        </span>
        {pattern.valence === 'attention' && (
          <span style={{ fontSize: '0.68rem', color: '#B8906A', fontWeight: '600' }}>· needs attention</span>
        )}
      </div>

      <h3 style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: '1.05rem', color: '#1E3A39',
        margin: '0 0 10px', fontWeight: '600', lineHeight: '1.4'
      }}>
        {pattern.title}
      </h3>

      <p style={{ margin: '0 0 14px', fontSize: '0.88rem', color: '#555', lineHeight: '1.7' }}>
        {pattern.insight}
      </p>

      {pattern.data_points?.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {pattern.data_points.map((dp, i) => (
            <div key={i} style={{
              display: 'flex', gap: '8px', alignItems: 'flex-start',
              fontSize: '0.78rem', color: '#8FA3A0'
            }}>
              <div style={{
                width: 4, height: 4, borderRadius: '50%',
                background: style.color, marginTop: 6, flexShrink: 0
              }} />
              {dp}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LoadingState() {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px' }}>
      <div style={{
        width: 48, height: 48, margin: '0 auto 20px',
        border: '2px solid rgba(184,211,207,0.3)',
        borderTop: '2px solid #2D8B87',
        borderRadius: '50%',
        animation: 'spin 1.2s linear infinite'
      }} />
      <p style={{ color: '#8FA3A0', fontSize: '0.9rem', fontStyle: 'italic' }}>
        Analysing your patterns…
      </p>
      <p style={{ color: '#B8D3CF', fontSize: '0.78rem', marginTop: '8px' }}>
        This may take 10–15 seconds.
      </p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function PatternIntelligence() {
  const navigate = useNavigate();
  const [user,        setUser]        = useState(null);
  const [data,        setData]        = useState(null);
  const [isLoading,   setIsLoading]   = useState(true);
  const [isRefreshing,setRefreshing]  = useState(false);
  const [isVisible,   setIsVisible]   = useState(false);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) { try { setUser(JSON.parse(u)); } catch {} }
    setTimeout(() => setIsVisible(true), 80);
  }, []);

  useEffect(() => {
    if (!user?.user_id) return;
    load();
  }, [user]);

  const load = async (forceRefresh = false) => {
    try {
      setIsLoading(true);
      const res = await axios.get(
        `${API}/intelligence/patterns/${user.user_id}?force_refresh=${forceRefresh}`
      );
      setData(res.data);
    } catch (e) {
      console.error(e);
      setData({ ready: false, message: 'Could not load pattern analysis.', patterns: [] });
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await load(true);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 60%, rgba(184,211,207,0.18) 100%)',
      padding: '28px 20px 100px', maxWidth: '680px', margin: '0 auto',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
      transition: 'opacity 0.5s ease, transform 0.5s ease'
    }}>

      <button onClick={() => navigate(-1)} style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#8FA3A0', fontSize: '0.82rem', marginBottom: '24px', padding: 0
      }}>
        <ArrowLeft size={14} color="#8FA3A0" /> Back
      </button>

      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <Spark size={20} color="#2D8B87" />
          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.6rem', color: '#1E3A39', margin: 0, fontWeight: '500'
          }}>Pattern Intelligence</h1>
        </div>
        <p style={{ margin: 0, color: '#8FA3A0', fontSize: '0.85rem', lineHeight: '1.6' }}>
          What your data reveals about your nervous system — patterns you might not yet have noticed.
        </p>
      </div>

      {isLoading ? (
        <div style={{
          background: '#fff', borderRadius: '20px',
          boxShadow: '0 2px 20px rgba(30,107,104,0.07)'
        }}>
          <LoadingState />
        </div>
      ) : !data?.ready ? (
        <div style={{
          background: '#fff', borderRadius: '20px', padding: '36px 28px',
          boxShadow: '0 2px 20px rgba(30,107,104,0.07)', textAlign: 'center'
        }}>
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
            <Chart size={40} color="#B8D3CF" />
          </div>
          <p style={{ color: '#8FA3A0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            {data?.message || 'Complete more sessions to unlock pattern analysis.'}
          </p>
        </div>
      ) : (
        <>
          {/* Summary card */}
          {data.summary && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(30,107,104,0.06) 0%, rgba(184,211,207,0.12) 100%)',
              borderRadius: '16px', padding: '20px 24px', marginBottom: '24px',
              border: '1px solid rgba(184,211,207,0.4)'
            }}>
              <div style={{
                fontSize: '0.68rem', color: '#8FA3A0', letterSpacing: '0.1em',
                textTransform: 'uppercase', fontWeight: '600', marginBottom: '8px'
              }}>
                Overview
              </div>
              <p style={{ margin: 0, fontSize: '0.95rem', color: '#333', lineHeight: '1.65', fontStyle: 'italic' }}>
                "{data.summary}"
              </p>
            </div>
          )}

          {/* Pattern cards */}
          {data.patterns?.map((p, i) => (
            <PatternCard key={p.id || i} pattern={p} index={i} />
          ))}

          {/* Refresh + timestamp */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginTop: '8px', padding: '0 4px'
          }}>
            <span style={{ fontSize: '0.72rem', color: '#B8D3CF', fontStyle: 'italic' }}>
              Generated {data.generated_at || 'today'} · refreshes daily
            </span>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'none', border: 'none', cursor: isRefreshing ? 'default' : 'pointer',
                color: '#8FA3A0', fontSize: '0.78rem', padding: '6px 10px',
                borderRadius: '10px', opacity: isRefreshing ? 0.5 : 1
              }}
            >
              <Cycle size={13} color="#8FA3A0" />
              {isRefreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {/* Clinical note */}
          <div style={{
            marginTop: '24px', padding: '16px 20px', borderRadius: '14px',
            background: 'rgba(30,107,104,0.04)', border: '1px solid rgba(184,211,207,0.3)'
          }}>
            <p style={{
              margin: 0, fontSize: '0.75rem', color: '#AAB8B6',
              lineHeight: '1.65', fontStyle: 'italic'
            }}>
              Pattern Intelligence analyses your practice data to surface regularities in your nervous system responses.
              These observations are descriptive, not diagnostic. They are intended to support self-awareness and
              inform your work with clinical professionals.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
